import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import athenaService from '../services/athenaService';

const LocationsManager = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const departments = await athenaService.getDepartments();
      
      // Filter out service departments and sort by name
      const activeLocations = departments
        .filter(dept => dept.servicedepartment === true)
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setLocations(activeLocations);
      
      // Select the first location by default if none is selected
      if (activeLocations.length > 0 && !selectedLocation) {
        setSelectedLocation(activeLocations[0]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load locations',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setEditingLocation(null);
  };

  const handleEditLocation = () => {
    setEditingLocation({...selectedLocation});
  };

  const handleInputChange = (field, value) => {
    setEditingLocation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancelEdit = () => {
    setEditingLocation(null);
  };

  const handleSaveLocation = async () => {
    setSaving(true);
    try {
      const updateData = {
        practiceid: editingLocation.providergroupid,
        providerid: editingLocation.departmentid,
        name: editingLocation.name,
        address: editingLocation.address,
        city: editingLocation.city,
        state: editingLocation.state,
        zip: editingLocation.zip,
        phone: editingLocation.phone,
        fax: editingLocation.fax || '',
        portalurl: editingLocation.portalurl || ''
      };

      // Call the athenaService method to update the provider/location
      const result = await athenaService.updateProvider(
        updateData.practiceid,
        updateData.providerid,
        updateData
      );

      if (result) {
        setSnackbar({
          open: true,
          message: 'Location updated successfully',
          severity: 'success'
        });

        // Refresh locations and update selected location
        await fetchLocations();
        const updatedLocation = locations.find(loc => loc.departmentid === editingLocation.departmentid);
        if (updatedLocation) {
          setSelectedLocation(updatedLocation);
        }
        
        setEditingLocation(null);
      } else {
        throw new Error('Failed to update location');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      setSnackbar({
        open: true,
        message: `Failed to update location: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  if (loading && locations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, height: 'calc(100vh - 10px)', overflow: 'auto' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          width: '100%', 
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'white',
          borderRadius: 0,
          borderBottom: `1px solid #e0e0e0`,
          mb: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Locations Manager</Typography>
        </Box>
      </Paper>
      
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: 3,
          bgcolor: 'white',
          borderRadius: 0,
          borderBottom: `1px solid #e0e0e0`,
          mb: 3
        }}
      >
        <Typography variant="body1" sx={{ mb: 3 }}>
          Manage your practice locations. Update address and contact information.
        </Typography>
      </Paper>
      
      <Box sx={{ px: 3 }}>
        <Grid container spacing={3}>
          {/* Locations List */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              sx={{ 
                border: '1px solid #e0e0e0',
                height: '100%',
                maxHeight: 'calc(100vh - 130px)',
                overflow: 'auto'
              }}
            >
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid #e0e0e0', 
                bgcolor: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="subtitle1" fontWeight="bold">Locations</Typography>
              </Box>
              <List sx={{ p: 0 }}>
                {locations.map((location, index) => (
                  <React.Fragment key={location.departmentid}>
                    {index > 0 && <Divider />}
                    <ListItemButton 
                      selected={selectedLocation?.departmentid === location.departmentid}
                      onClick={() => handleLocationSelect(location)}
                    >
                      <ListItemText 
                        primary={location.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                              {location.address}
                            </Typography>
                            <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                              {location.city}, {location.state} {location.zip}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
          
          {/* Location Details */}
          <Grid item xs={12} md={8}>
            {selectedLocation ? (
              <Paper 
                elevation={0} 
                sx={{ 
                  border: '1px solid #e0e0e0', 
                  p: 3,
                  height: '100%',
                  maxHeight: 'calc(100vh - 130px)',
                  overflow: 'auto'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h5">
                    {editingLocation ? 'Edit Location' : 'Location Details'}
                  </Typography>
                  
                  {!editingLocation ? (
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<EditIcon />}
                      onClick={handleEditLocation}
                      sx={{ display: 'none' }}  // Hide the edit button
                    >
                      Edit
                    </Button>
                  ) : (
                    <Box>
                      <Button 
                        variant="outlined"
                        onClick={handleCancelEdit}
                        sx={{ mr: 1 }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSaveLocation}
                        disabled={saving}
                      >
                        Save
                      </Button>
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                {editingLocation ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Location Name"
                        value={editingLocation.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={editingLocation.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="City"
                        value={editingLocation.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="State"
                        value={editingLocation.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="ZIP Code"
                        value={editingLocation.zip}
                        onChange={(e) => handleInputChange('zip', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={editingLocation.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Fax"
                        value={editingLocation.fax || ''}
                        onChange={(e) => handleInputChange('fax', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Portal URL"
                        value={editingLocation.portalurl || ''}
                        onChange={(e) => handleInputChange('portalurl', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LanguageIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">{selectedLocation.name}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <LocationIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body1">{selectedLocation.address}</Typography>
                          <Typography variant="body1">
                            {selectedLocation.city}, {selectedLocation.state} {selectedLocation.zip}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">{selectedLocation.phone}</Typography>
                      </Box>
                      
                      {selectedLocation.fax && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body1">Fax: {selectedLocation.fax}</Typography>
                        </Box>
                      )}
                      
                      {selectedLocation.portalurl && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body1">
                            <a href={selectedLocation.portalurl.startsWith('http') ? selectedLocation.portalurl : `https://${selectedLocation.portalurl}`} target="_blank" rel="noopener noreferrer">
                              {selectedLocation.portalurl}
                            </a>
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="subtitle1" gutterBottom>
                        Additional Information
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Department ID</Typography>
                          <Typography variant="body1">{selectedLocation.departmentid}</Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Provider Group</Typography>
                          <Typography variant="body1">{selectedLocation.providergroupname}</Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Place of Service</Typography>
                          <Typography variant="body1">{selectedLocation.placeofservicetypename || 'N/A'}</Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Timezone</Typography>
                          <Typography variant="body1">{selectedLocation.timezonename}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            ) : (
              <Paper 
                elevation={0} 
                sx={{ 
                  border: '1px solid #e0e0e0', 
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Select a location to view details
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationsManager;
